import { useEffect } from 'react';
import 'cookieconsent/build/cookieconsent.min.css';
import { initSentry, setSentryUser } from '@/config/sentry';

interface CookieBannerProps {
    onAnalyticsConsent?: (granted: boolean) => void;
    userId?: number | null;
}

// Extend window to include cookieconsent
declare global {
    interface Window {
        cookieconsent: any;
    }
}

export default function CookieBanner({ onAnalyticsConsent, userId }: CookieBannerProps) {
    useEffect(() => {
        // Load cookieconsent script directly since the npm import seems problematic
        const script = document.createElement('script');
        script.src = 'https://cdn.jsdelivr.net/npm/cookieconsent@3/build/cookieconsent.min.js';
        script.onload = () => {
            if (window.cookieconsent && window.cookieconsent.initialise) {
                window.cookieconsent.initialise({
                palette: {
                    popup: {
                        background: "#1e40af", // Blue background
                        text: "#ffffff"
                    },
                    button: {
                        background: "#ffc20e", // Yellow button (matching your site colors)
                        text: "#1b1b18"
                    }
                },
                theme: "classic",
                position: "bottom",
                type: "opt-in",
                static: false, // Allow banner to be dismissed
                revokable: false, // Don't show revoke button after consent
                dismissOnScroll: false, // Don't auto-dismiss on scroll
                dismissOnTimeout: false, // Don't auto-dismiss on timeout
                showLink: false, // Don't show persistent cookie preferences link
                content: {
                    message: "We use cookies to enhance your experience. By continuing to visit this site you agree to our use of cookies.",
                    allow: "Accept All",
                    deny: "Decline",
                    link: "Learn more",
                    href: "/privacy-policy" // You may need to create this page
                },
                law: {
                    regionalLaw: false,
                },
                location: false,
                onInitialise: function(status: string) {
                    const type = this.options.type;
                    const didConsent = this.hasConsented();

                    if (type === 'opt-in' && didConsent) {
                        // User has opted in to analytics
                        onAnalyticsConsent?.(true);
                        // Initialize Sentry since user consented
                        initSentry();
                        setSentryUser(userId ?? null);
                        // Hide banner since user already consented
                        setTimeout(() => {
                            const bannerElements = document.querySelectorAll('.cc-window, .cc-banner, .osano-cm-widget, .cc-revoke');
                            bannerElements.forEach(element => {
                                (element as HTMLElement).style.display = 'none';
                            });
                        }, 100);
                    } else if (type === 'opt-out' && !didConsent) {
                        // User has opted out of analytics
                        onAnalyticsConsent?.(false);
                        // Hide banner since user already made choice
                        setTimeout(() => {
                            const bannerElements = document.querySelectorAll('.cc-window, .cc-banner, .osano-cm-widget, .cc-revoke');
                            bannerElements.forEach(element => {
                                (element as HTMLElement).style.display = 'none';
                            });
                        }, 100);
                    } else {
                        // Default behavior - no consent given yet, show banner
                        onAnalyticsConsent?.(false);
                    }
                },
                onStatusChange: function(status: string, chosenBefore: boolean) {
                    const type = this.options.type;
                    const didConsent = this.hasConsented();

                    if (type === 'opt-in' && didConsent) {
                        // User opted in to analytics
                        onAnalyticsConsent?.(true);
                        // Initialize Sentry when user accepts
                        initSentry();
                        setSentryUser(userId ?? null);
                    } else {
                        // User declined or opted out
                        onAnalyticsConsent?.(false);
                    }

                    // Ensure banner is completely hidden after choice is made
                    setTimeout(() => {
                        const bannerElements = document.querySelectorAll('.cc-window, .cc-banner, .osano-cm-widget, .cc-revoke');
                        bannerElements.forEach(element => {
                            (element as HTMLElement).style.display = 'none';
                        });
                    }, 200);
                },
                onRevokeChoice: function() {
                    // User revoked choice - disable analytics
                    onAnalyticsConsent?.(false);
                }
            });
            } else {
                console.error('CookieConsent not loaded properly');
            }
        };

        script.onerror = () => {
            console.error('Failed to load cookieconsent script');
        };

        // Only add script if not already loaded
        if (!window.cookieconsent && !document.querySelector('script[src*="cookieconsent"]')) {
            document.head.appendChild(script);
        } else if (window.cookieconsent) {
            // Already loaded, initialize directly
            script.onload(new Event('load'));
        }
    }, [onAnalyticsConsent]);

    return null; // This component doesn't render anything itself
}
